import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { COMMAND_BY_ID, formatSequence } from './commandModel.js'

export const COMMAND_HINT_DELAY = 2000

// Delegation also covers controls rendered through portals (layout/VWAP menus).
// New command controls opt in by exposing their registry ID, without wrappers.
export default function CommandHoverHint({ bindings, platform }) {
  const [target, setTarget] = useState(null)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const hintRef = useRef(null)
  const id = useId()
  const location = useLocation()
  useEffect(() => {
    let timer, candidate
    const dismiss = () => { clearTimeout(timer); candidate = null; setTarget(null) }
    const enter = event => {
      const element = event.target?.closest?.('[data-command-id]')
      if (!element || !COMMAND_BY_ID[element.dataset.commandId]) return
      if (candidate === element) return
      dismiss()
      candidate = element
      timer = setTimeout(() => { if (element.isConnected) setTarget(element) }, COMMAND_HINT_DELAY)
    }
    const leave = event => {
      if (candidate?.contains(event.target) && !candidate.contains(event.relatedTarget)) dismiss()
    }
    const events = [['pointerover', enter], ['pointerout', leave], ['focusin', enter], ['focusout', leave],
      ['pointerdown', dismiss], ['scroll', dismiss]]
    events.forEach(([name, handler]) => document.addEventListener(name, handler, true))
    window.addEventListener('keydown', dismiss, true)
    window.addEventListener('resize', dismiss)
    window.addEventListener('blur', dismiss)
    dismiss()
    return () => {
      clearTimeout(timer)
      events.forEach(([name, handler]) => document.removeEventListener(name, handler, true))
      window.removeEventListener('keydown', dismiss, true)
      window.removeEventListener('resize', dismiss)
      window.removeEventListener('blur', dismiss)
    }
  }, [location.key])
  const commandId = target?.dataset.commandId
  const label = target?.dataset.commandLabel || target?.getAttribute('aria-label') || COMMAND_BY_ID[commandId]?.title
  const shortcut = bindings.filter(binding => binding.commandId === commandId)
    .map(binding => formatSequence(binding.sequence, platform)).join(' / ')
  useLayoutEffect(() => {
    if (!target || !hintRef.current) return
    const rect = target.getBoundingClientRect()
    const hint = hintRef.current.getBoundingClientRect()
    setPosition({
      left: Math.max(8, Math.min(rect.left + rect.width / 2 - hint.width / 2, window.innerWidth - hint.width - 8)),
      top: rect.bottom + hint.height + 16 <= window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - hint.height - 8),
    })
    const existing = target.getAttribute('aria-describedby')
    target.setAttribute('aria-describedby', [existing, id].filter(Boolean).join(' '))
    return () => {
      if (existing) target.setAttribute('aria-describedby', existing)
      else target.removeAttribute('aria-describedby')
    }
  }, [target, label, shortcut, id])
  if (!target?.isConnected || !label) return null
  return createPortal(<div ref={hintRef} id={id} role="tooltip" style={position}
    className="pointer-events-none fixed z-[1200] flex max-w-[calc(100vw-16px)] items-center gap-3 rounded border border-border bg-canvas px-2.5 py-1.5 text-xs text-ink shadow-lg">
    <span>{label}</span>
    {shortcut && <kbd className="shrink-0 font-sans text-inkMuted">{shortcut}</kbd>}
  </div>, document.body)
}
